import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ComputerIcon from '@mui/icons-material/Computer'
import { Box, Button, Card, CardContent, Typography } from '@mui/material'
import React from 'react'

import Database from '../../types/Database'

interface DeskCreationPromptStepProps {
  office: Database.Office
  onProceed: () => void
  onSkip: () => void
}

/**
 * DeskCreationPromptStep - Prompts users to create desks after successfully creating an office
 *
 * Displays a success message for office creation and explains why creating desks is important.
 * Offers options to proceed to desk creation or skip for now.
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
 */
const DeskCreationPromptStep: React.FC<DeskCreationPromptStepProps> = ({
  office,
  onProceed,
  onSkip
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4
      }}
    >
      {/* Success Header */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 48,
              color: 'success.contrastText'
            }}
          />
        </Box>
        <Typography
          variant='h4'
          sx={{
            fontWeight: 600,
            mb: 2,
            color: 'text.primary'
          }}
        >
          Office Created Successfully!
        </Typography>
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
            maxWidth: 600,
            mx: 'auto'
          }}
        >
          Your office "{office.name}" is ready. Now let's add some desks.
        </Typography>
      </Box>

      {/* Why Desks Are Important Card */}
      <Card
        sx={{
          width: '100%',
          maxWidth: 700,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            gap: 2,
            p: 3
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <ComputerIcon
              sx={{
                fontSize: 24,
                color: 'primary.contrastText'
              }}
            />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1
              }}
            >
              Why Create Desks?
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
                lineHeight: 1.6,
                mb: 1
              }}
            >
              Desks are the bookable resources in your office. Creating desks
              allows you and your team to:
            </Typography>
            <Box
              component='ul'
              sx={{
                color: 'text.secondary',
                pl: 2,
                m: 0,
                '& li': {
                  mb: 0.5,
                  fontSize: '0.875rem',
                  lineHeight: 1.6
                }
              }}
            >
              <li>Reserve specific workspaces for each day</li>
              <li>
                See who's coming into the office and where they'll be sitting
              </li>
              <li>Coordinate with team members and plan collaboration</li>
              <li>Manage office capacity and space utilization</li>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          width: '100%',
          maxWidth: 700,
          mt: 2
        }}
      >
        <Button
          variant='outlined'
          size='large'
          onClick={onSkip}
          sx={{
            flex: 1,
            textTransform: 'none',
            py: 1.5,
            fontWeight: 500
          }}
        >
          Skip for Now
        </Button>
        <Button
          variant='contained'
          size='large'
          onClick={onProceed}
          sx={{
            flex: 1,
            textTransform: 'none',
            py: 1.5,
            fontWeight: 500
          }}
        >
          Create Desks
        </Button>
      </Box>
    </Box>
  )
}

export default DeskCreationPromptStep
